import { create } from 'zustand';
import subscriptionService from '../services/subscription.service';

/**
 * User Features Store
 * Quản lý features của user dựa trên subscription hiện tại
 * CHỈ gọi API một lần khi user đăng nhập hoặc khi app load
 */
const useUserFeaturesStore = create((set, get) => ({
  // State
  subscription: null,
  features: [],
  uiElements: [], // Tất cả UI elements user có quyền truy cập
  loading: false,
  error: null,
  initialized: false, // Đã fetch lần đầu chưa

  // Actions
  /**
   * Fetch features của user từ API
   * CHỈ gọi một lần duy nhất, trừ khi force = true
   * @param {boolean} force - Bỏ qua cache và fetch lại
   */
  fetchUserFeatures: async (force = false) => {
    const { initialized, loading } = get();

    // Nếu đã initialized và không force -> skip
    if (initialized && !force) {
      return { features: get().features, uiElements: get().uiElements };
    }

    // Nếu đang loading -> skip để tránh gọi đồng thời
    if (loading) {
      return { features: get().features, uiElements: get().uiElements };
    }

    set({ loading: true, error: null });

    try {
      const response = await subscriptionService.getMyFeatures();
      const payload = response.payload || {};

      set({
        subscription: payload.subscription,
        features: payload.features || [],
        uiElements: payload.ui_elements || [],
        loading: false,
        initialized: true,
        error: null,
      });

      return { features: payload.features || [], uiElements: payload.ui_elements || [] };
    } catch (error) {
      set({
        error: error.message || 'Failed to fetch user features',
        loading: false,
        initialized: true, // Đánh dấu đã thử fetch để không gọi lại
      });
      return { features: [], uiElements: [] };
    }
  },

  /**
   * Kiểm tra user có quyền xem element không
   * @param {string} elementName - Tên component/element
   * @returns {boolean}
   */
  canAccessElement: (elementName) => {
    const { uiElements } = get();
    // Nếu chưa load features, cho phép tạm thời (sẽ check lại sau)
    if (uiElements.length === 0) {
      return true;
    }
    return uiElements.includes(elementName);
  },

  /**
   * Kiểm tra user có feature code không
   * @param {string} featureCode - Code của feature (VD: FEATURE_PORTFOLIO)
   * @returns {boolean}
   */
  hasFeature: (featureCode) => {
    const { features } = get();
    return features.some(f => f.code === featureCode);
  },

  /**
   * Lấy thông tin feature theo code
   * @param {string} featureCode - Code của feature
   * @returns {Object|null}
   */
  getFeature: (featureCode) => {
    const { features } = get();
    return features.find(f => f.code === featureCode) || null;
  },

  /**
   * Clear cache khi logout
   */
  clearCache: () => {
    set({
      subscription: null,
      features: [],
      uiElements: [],
      initialized: false,
      error: null,
    });
  },
}));

export default useUserFeaturesStore;

